import type { NostrEvent } from "@nostrify/nostrify";

// Custom Nostr kind for BCH Trail leaderboard scores (see NIP.md).
export const SCORE_KIND = 9877;
// Namespace tag value used for relay-level filtering.
export const SCORE_TAG = "bch-trail";

export interface ScoreEntry {
  id: string;
  pubkey: string;
  createdAt: number;
  score: number;
  outcome: "victory" | "defeat";
  days: number;
  sats: number;
  survivors: number | null;
  party: number | null;
  difficulty: string | null;
  content: string;
}

function tagValue(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([n]) => n === name)?.[1];
}

/**
 * Validate + parse a kind 9877 event into a ScoreEntry.
 * Returns null if the event is missing required numeric fields.
 */
export function parseScoreEvent(event: NostrEvent): ScoreEntry | null {
  if (event.kind !== SCORE_KIND) return null;
  if (tagValue(event, "t") !== SCORE_TAG) return null;

  const scoreStr = tagValue(event, "score");
  const outcome = tagValue(event, "outcome");
  const daysStr = tagValue(event, "days");
  const satsStr = tagValue(event, "sats");

  const score = Number(scoreStr);
  const days = Number(daysStr);
  const sats = Number(satsStr);

  if (!Number.isFinite(score) || score < 0) return null;
  if (!Number.isFinite(days) || days <= 0) return null;
  if (!Number.isFinite(sats) || sats < 0) return null;
  if (outcome !== "victory" && outcome !== "defeat") return null;

  const survivorsStr = tagValue(event, "survivors");
  const partyStr = tagValue(event, "party");
  const survivors = survivorsStr != null ? Number(survivorsStr) : null;
  const party = partyStr != null ? Number(partyStr) : null;

  return {
    id: event.id,
    pubkey: event.pubkey,
    createdAt: event.created_at,
    score: Math.round(score),
    outcome,
    days: Math.round(days),
    sats: Math.round(sats),
    survivors: survivors != null && Number.isFinite(survivors) ? Math.round(survivors) : null,
    party: party != null && Number.isFinite(party) ? Math.round(party) : null,
    difficulty: tagValue(event, "difficulty") ?? null,
    content: event.content,
  };
}
